import {
  differenceInCalendarMonths,
  format,
  parse,
  isEqual,
  parseISO,
  isAfter,
  isBefore
} from 'date-fns';
import { DateTime } from 'luxon';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
export const AM = 'AM';
export const PM = 'PM';

class DateUtils {
  /**
   * @param date
   * @returns Example: 2021-04-02
   */
  static getShortDate(date: Date): string {
    // Note: Previously, this method used the `toISOString()` function
    // of the date object, but with a withdraw
    // Working with JS dates is a funny game haha
    // As you may know, the timezone for the dates in the frontend
    // is set by the browser. That way all your dates are in sync with your current time
    // But, the `toISOString()` function does a funny thing by giving you the formatted date
    // in UTC.

    // Since we need the date formatted to ISO8601, our only way
    // to quickly do it is through the Intl API by using a locale
    // where the default date format is the desired...
    const isoDate = Intl.DateTimeFormat('sv-SE').format(date);

    // But this can change at any time in the future
    if (isoDate.match(/^([0-9]{4})-?(1[0-2]|0[1-9])-?(3[01]|0[1-9]|[12][0-9])$/)) {
      return isoDate;
    }

    // So we have a 2nd option in case this changes at any moment...
    return (
      date.getFullYear() +
      '-' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + date.getDate()).slice(-2)
    );
  }

  /**
   * @param date
   * @returns Example: Apr-02-21
   */
  static getShortDateWithMedMonth = (date: Date): string => {
    // return date in format Apr-02-21

    const dateInLocale = this.getDateInLocale(date);
    return format(dateInLocale, 'MMM-dd-yy');
  };

  /**
   * @param startDate
   * @param endDate - Optional
   * @returns Example receiving startDate only: Apr 2, 2021
   * @returns Example receiving startDate and endDate: Apr 2 - Apr 5, 2021
   */
  static getRangeMidiumDate = (startDate: Date, endDate: Date | null, timeZone = 'UTC'): string => {
    const start = startDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: timeZone
    });

    if (!endDate) {
      return `${start}, ${startDate.getFullYear()}`;
    }

    const end = endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: timeZone
    });

    return `${start} - ${end}, ${endDate.getFullYear()}`;
  };

  /**
   * @param date
   * @returns Example: Apr 2, 2021
   */
  static getMediumDate = (date: Date, timeZone = 'UTC', includeYear = true): string => {
    const options: any = {
      month: 'short',
      day: 'numeric',
      timeZone: timeZone
    };

    if (includeYear) {
      options.year = 'numeric';
    }
    return date.toLocaleDateString('en-US', options);
  };

  /**
   * @param date
   * @returns Example: Apr 2, 2021
   */
  static getMediumDateInLocalTimeZone = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  /**
   * Returns a new date from the given localized date
   * @param date
   * @returns Date
   */
  static getDateInLocale = (date: Date): Date => {
    return new Date(
      date.toLocaleDateString('en-US', {
        timeZone: 'UTC'
      })
    );
  };

  /**
   * Probably need a rework
   * Returns a new date time from the given localized date
   * @param date
   * @returns Date
   */
  static getLocaleDateTime = (date: Date): Date => {
    const localeDate = date.toLocaleDateString('en-US', {
      timeZone: 'UTC'
    });
    const localeTime = date.toLocaleTimeString('en-US', {
      timeZone: 'UTC'
    });

    return new Date(
      localeDate + ' ' + localeTime.replace(/([0-9]{1,2}):([0-9]{1,2}):([0-9]{1,2})/, '$1:$2')
    );
  };

  /**
   * Returns a new date time from the given localized date
   * @param date
   * @param time
   * @returns ISO date
   */
  static getISODate = (date: Date, time?: Date): string => {
    if (!time) return date.toISOString();

    const d = new Date(date);
    d.setHours(time.getHours(), time.getMinutes(), time.getSeconds());

    return d.toISOString();
  };

  /**
   * @param date
   * @returns Example: 03:14 AM,
   */
  static getTime = (date: Date, overrides?: Intl.DateTimeFormatOptions): string => {
    let options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit'
    };

    if (overrides) {
      options = { ...options, ...overrides };
    }

    // Restricted to US Time Zone
    const hours = date.toLocaleTimeString('en-US', options);

    return hours;
  };

  /**
   * @param date
   * @returns Example: 03:14 AM,
   */
  static get12HourFormattedTime = (date: Date): string => {
    return new Intl.DateTimeFormat('default', {
      hour12: true,
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };

  /**
   * Converts 24 hour time string to AM/PM
   * @param config - Example: {time: '03:00', fourDigit: true}
   * @returns Example: 3 AM or 03:00 AM
   */
  static get12HourFormat = (config: { time: string; fourDigit?: boolean }): string => {
    const parts = config.time.split(':');
    const tempDate = new Date();
    tempDate.setHours(parseInt(parts[0]), parseInt(parts[1]));

    const options: Intl.DateTimeFormatOptions = {
      hour12: true,
      hour: 'numeric'
    };

    if (config.fourDigit) {
      options.minute = '2-digit';
      options.hour = '2-digit';
    }

    return new Intl.DateTimeFormat('default', options).format(tempDate);
  };

  /**
   * @param hours 12:00 AM / 03:45 PM
   * @returns Example: 00:00 / 15:45
   */
  static get24HourFormat = (time: string): string => {
    const slicedTime = time.split(/(PM|AM)/gm)[0];

    let hours = slicedTime.split(':')[0].trim();
    const minutes = slicedTime.split(':')[1] ? slicedTime.split(':')[1].trim() : '00';

    if (hours === '12') {
      hours = '00';
    }

    let updateHourAndMin;

    const addition = (hoursOrMin: string): string => {
      updateHourAndMin = hoursOrMin.length < 2 ? (hoursOrMin = `${0}${hoursOrMin}`) : hoursOrMin;
      return updateHourAndMin;
    };

    if (time.endsWith('PM')) {
      hours = (parseInt(hours, 10) + 12).toString();
    }

    return `${addition(hours)}:${addition(minutes)}`.split(' ')[0];
  };

  /**
   * @param date
   * @returns Example: Apr 2, 2021
   */
  static getNumberOfDaysUntilNow = (refDate: Date): number => {
    const now = new Date();
    const days = (now.getTime() - refDate.getTime()) / MILLISECONDS_PER_DAY;

    return days < 1 ? days : Math.round(days);
  };

  /**
   * @param numberOfDays
   * @returns a date
   */
  static getTodayPlusDays = (numberOfDays: number): Date => {
    const today = new Date();
    const days = new Date(today.getTime());

    days.setDate(today.getDate() + numberOfDays);

    return days;
  };

  static getDatePlusDays = (date: Date, numberOfDays: number): Date => {
    const days = new Date(date.getTime());

    days.setDate(date.getDate() + numberOfDays);

    return days;
  };

  /**
   * @param date
   * @returns Example: April
   */
  static getMonthLabel = (date: Date): string => {
    const month = date.getMonth();

    switch (month) {
      case 0:
        return 'January';
      case 1:
        return 'February';
      case 2:
        return 'March';
      case 3:
        return 'April';
      case 4:
        return 'May';
      case 5:
        return 'June';
      case 6:
        return 'July';
      case 7:
        return 'August';
      case 8:
        return 'September';
      case 9:
        return 'October';
      case 10:
        return 'November';
      case 11:
        return 'December';
      default:
        return 'January';
    }
  };

  /**
   * Returns the list of times of the day by a certain interval of minutes
   * @param interval
   * @returns list of times AM and PM
   */
  static getDayTimesByInterval = (
    interval = 15,
    extraOptions?: Intl.DateTimeFormatOptions
  ): string[] => {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      ...extraOptions
    };
    const date = new Date(1970, 0, 1);
    const times = [];
    while (date.getDate() === 1) {
      times.push(DateUtils.getTime(date, options));
      date.setMinutes(date.getMinutes() + interval);
    }
    return times;
  };

  /**
   * Returns date in full locale format:
   * @param dateString
   * @returns formatted locale date string
   */
  static getFullLocaleDate = (dateString: string): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    };
    const date = new Date(dateString);
    return date.toLocaleString('en-US', options);
  };

  static validateHhMm = (time: string): boolean => /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/.test(time);

  static validateAmPm = (time: string): boolean => /^([0-1]\d):([0-5]\d)\s?(?:AM|PM)?$/i.test(time);

  /**
   * Validates time from a date object in 24 hour format
   * @param date
   * @returns boolean
   */
  static validateTimeFromDate = (date: Date): boolean => {
    return DateUtils.validateHhMm(DateUtils.get24HourFormat(DateUtils.getTime(date)));
  };

  /**
   *
   * @param date string and in period format : 2023-02-23 - 2023-02-28
   * @returns Example: Feb 23 - 28 if the same month. Or Feb 23 - Sept 28 if different months
   *
   */
  static formatPeriodToMonthDay = (
    date: string,
    showYear?: boolean,
    showFullMonth?: boolean
  ): string => {
    const date1 = date.slice(0, date.indexOf(' ') + 1);
    const date2 = date.slice(date.indexOf(' ') + 3);

    const dateStartParse = parse(date1, 'yyyy-MM-dd', new Date());
    const dateEndParse = parse(date2, 'yyyy-MM-dd', new Date());

    const difference = differenceInCalendarMonths(dateStartParse, dateEndParse);

    let formatDateString = '';
    if (difference === 0) {
      formatDateString = `${format(dateStartParse, showFullMonth ? 'MMMM d' : 'MMM d')} - ${format(
        dateEndParse,
        'd'
      )}`;
    } else {
      formatDateString = `${format(dateStartParse, showFullMonth ? 'MMMM d' : 'MMM d')} - ${format(
        dateEndParse,
        showFullMonth ? 'MMMM d' : 'MMM d'
      )}`;
    }

    if (showYear) {
      formatDateString = `${formatDateString}, ${dateStartParse.getFullYear()}`;
    }

    return formatDateString;
  };

  static validateTimeFormat(time: string, use24HourFormat?: boolean): boolean {
    if (use24HourFormat) {
      return DateUtils.validateHhMm(time);
    } else {
      return DateUtils.validateAmPm(time);
    }
  }

  /**
   * @param date
   * @returns Example: 12April2023-13:05
   */
  static formatDateForScan = (date: Date): string => {
    const dateInLocale = this.getDateInLocale(date);
    const text = format(dateInLocale, 'ddMMMyyyy');
    return `${text}-${DateUtils.getTime(date)}`;
  };

  /** Converts time to a Date object
   * @param time
   * @returns Date
   */
  static convertTimeToDate = (time: string): Date => {
    const date = new Date();
    const [hours, minutes] = time.split(':');

    date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    return date;
  };

  /** Checks if two dates are the same
   * @param date1
   * @param date2
   * @returns boolean
   *
   */
  static validateSameDate = (date1: Date, date2: Date): boolean => {
    return isEqual(date1, date2);
  };

  /**
   * Format date into day and time
   * @param date String
   * @returns Example: Apr 2, 2021 • 03:14 AM
   */
  static splitDateAndTime = (date: string, timeZone?: string, connectWith?: string): string => {
    const originalDate = new Date(date);
    const formattedTime = this.getTime(originalDate, { timeZone: timeZone });

    return `${this.getMediumDateInLocalTimeZone(originalDate)} ${
      connectWith ? connectWith : '•'
    } ${formattedTime}`;
  };

  static getBrowserTimeZoneOffsetNameShort = (): string => {
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneOffsetNameShort = DateTime.now().setZone(clientTimeZone).offsetNameShort || '';
    return timezoneOffsetNameShort;
  };

  static formatTimeForTooltip = (
    use24HourClock: boolean,
    time?: string | null,
    date?: string
  ): string => {
    if (!time) return '';
    if (!use24HourClock && !date) return '';

    if (!use24HourClock) {
      return DateUtils.get12HourFormattedTime(new Date(`${date}T${time.split('+')[0]}`));
    } else {
      return time?.slice(0, -3);
    }
  };

  /**
   *
   * @param date string
   * @param use24clock boolean
   * @param enableRange boolean
   * @param time undefined | string
   * @param range undefined | string
   * @returns Dec 18, 2024 • 14:05 - 14:35 OR Dec 18, 2024 • 02:05 PM - 02:35 PM
   */
  static formatDateTimeAndRangeForStops = (
    date: string,
    use24clock: boolean,
    enableRange: boolean,
    time?: string | null,
    range?: string | null
  ): string => {
    let dateToShow = '';

    if (date) {
      dateToShow = `${DateUtils.getMediumDate(new Date(date))}`;
    }
    if (time) {
      dateToShow = `${dateToShow} • ${
        use24clock
          ? time?.slice(0, -3)
          : DateUtils.get12HourFormattedTime(new Date(`${date}T${time.split('+')[0]}`))
      }`;
    }

    if (range && enableRange) {
      dateToShow = `${dateToShow} - ${
        use24clock
          ? range?.slice(0, -3)
          : DateUtils.get12HourFormattedTime(new Date(`${date}T${range.split('+')[0]}`))
      }`;
    }

    return dateToShow;
  };

  /**
   * Function to check if today is between the start and end date
   *
   * @param startDate Date
   * @param endDate Date | null
   * @returns boolean
   */
  static isDateActive = (
    startDateString: Date,
    endDateString: Date | null,
    dateReference?: string
  ): boolean => {
    const startDate = parseISO(new Date(startDateString).toISOString()); // Convert to date UTC
    const endDate = endDateString ? parseISO(new Date(endDateString).toISOString()) : null;
    const today = dateReference ? new Date(dateReference) : new Date();
    today.setUTCHours(0, 0, 0, 0); // Adjust to 00:00:00 UTC
    const todayUTC = new Date(today.toISOString());

    // we consider the date active if today is after the start date
    if (isAfter(startDate, todayUTC)) {
      return true;
    }

    if (endDate) {
      const isTodayAfterOrEqualStart = isAfter(todayUTC, startDate) || isEqual(todayUTC, startDate);
      const isTodayBeforeOrEqualEnd = isBefore(todayUTC, endDate) || isEqual(todayUTC, endDate);

      return isTodayAfterOrEqualStart && isTodayBeforeOrEqualEnd;
    }

    const isTodayAfterOrEqualStart = isAfter(todayUTC, startDate) || isEqual(todayUTC, startDate);

    return isTodayAfterOrEqualStart;
  };

  static formatDateForTable = (date: string): string => {
    return format(parseISO(date), 'MM/dd/yyyy');
  };

  static formatPeriodDate = (period: string): string => {
    const splitPeriod = period.split('T')[0];
    const year = splitPeriod.split('-')[0];
    const month = splitPeriod.split('-')[1];
    const day = splitPeriod.split('-')[2];
    return `${month}/${day}/${year}`;
  };
}

export default DateUtils;
